import React, { useState, useEffect } from 'react';
import { Plus, Upload, Trash2, Edit2, ChevronDown, ChevronUp, AlertCircle, CheckCircle } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './AMCManagement.css';

const AMCManagement = () => {
  const [amcs, setAmcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedAmc, setExpandedAmc] = useState(null);

  // Create AMC modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAmcName, setNewAmcName] = useState('');
  const [newAmcDescription, setNewAmcDescription] = useState('');
  const [creatingAmc, setCreatingAmc] = useState(false);

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedAmc, setSelectedAmc] = useState(null);
  const [uploadType, setUploadType] = useState('pdf'); // 'pdf' or 'json'
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'amc' | 'pdf' | 'json', ... }

  useMountEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please login to access this page');
      setLoading(false);
      return;
    }
    fetchAmcs();
  });

  const fetchAmcs = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/amcs');
      const amcsWithTemplates = await Promise.all(
        (data.amcs || []).map(async (amc) => {
          try {
            const pdfData = await apiCall(`/api/amcs/${amc._id}/pdf-templates`);
            const jsonData = await apiCall(`/api/amcs/${amc._id}/json-templates`);
            return {
              ...amc,
              pdfTemplates: pdfData.templates || [],
              jsonTemplates: jsonData.templates || []
            };
          } catch {
            return {
              ...amc,
              pdfTemplates: [],
              jsonTemplates: []
            };
          }
        })
      );
      setAmcs(amcsWithTemplates);
    } catch (err) {
      console.error('Error fetching AMCs:', err);
      setError('Failed to load AMCs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAmc = async () => {
    if (!newAmcName.trim()) {
      setError('AMC name is required');
      return;
    }

    setCreatingAmc(true);
    try {
      const result = await apiCall('/api/amcs', {
        method: 'POST',
        body: JSON.stringify({
          name: newAmcName.trim(),
          description: newAmcDescription.trim()
        })
      });

      setSuccess(`AMC "${result.name}" created successfully`);
      setNewAmcName('');
      setNewAmcDescription('');
      setShowCreateModal(false);
      await fetchAmcs();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to create AMC');
    } finally {
      setCreatingAmc(false);
    }
  };

  const handleUploadTemplate = async () => {
    if (!uploadFile) {
      setError('Please select a file');
      return;
    }

    if (!selectedAmc) {
      setError('Please select an AMC');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const endpoint = uploadType === 'pdf'
        ? `/api/amcs/${selectedAmc._id}/pdf-templates/upload`
        : `/api/amcs/${selectedAmc._id}/json-templates/upload`;

      const token = localStorage.getItem('token');
      const response = await apiCall(endpoint, {
        method: 'POST',
        body: formData,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      setSuccess(`${uploadType.toUpperCase()} "${uploadFile.name}" uploaded successfully`);
      setUploadFile(null);
      setShowUploadModal(false);
      await fetchAmcs();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAmc = async (amcId, amcName) => {
    try {
      await apiCall(`/api/amcs/${amcId}`, { method: 'DELETE' });

      setSuccess(`AMC "${amcName}" and all its templates deleted`);
      setDeleteConfirm(null);
      await fetchAmcs();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleDeleteTemplate = async (amcId, templateId, type) => {
    try {
      const endpoint = type === 'pdf'
        ? `/api/amcs/${amcId}/pdf-templates/${templateId}`
        : `/api/amcs/${amcId}/json-templates/${templateId}`;

      await apiCall(endpoint, { method: 'DELETE' });

      setSuccess(`${type.toUpperCase()} template deleted`);
      setDeleteConfirm(null);
      await fetchAmcs();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="amc-management">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading AMCs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="amc-management">
      <div className="amc-header">
        <h1>AMC Management</h1>
        <p>Create and manage Asset Management Companies and their templates</p>
        <button
          className="btn-create-amc"
          onClick={() => setShowCreateModal(true)}
        >
          <Plus size={18} />
          Create New AMC
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle size={18} />
          <span>{success}</span>
        </div>
      )}

      {/* AMCs List */}
      <div className="amcs-container">
        {amcs.length === 0 ? (
          <div className="empty-state">
            <p>No AMCs found. Create one to get started.</p>
          </div>
        ) : (
          amcs.map(amc => (
            <div key={amc._id} className="amc-card">
              <div
                className="amc-header-row"
                onClick={() => setExpandedAmc(expandedAmc === amc._id ? null : amc._id)}
              >
                <div className="amc-info">
                  <h3>{amc.name}</h3>
                  {amc.description && <p>{amc.description}</p>}
                </div>
                <div className="amc-actions-header">
                  <button
                    className="btn-upload"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAmc(amc);
                      setShowUploadModal(true);
                    }}
                    title="Upload template"
                  >
                    <Upload size={16} />
                    Upload
                  </button>
                  <button
                    className="btn-delete-amc"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (amc.name === 'BAJAJ') {
                        setError('Cannot delete default BAJAJ AMC');
                      } else {
                        setDeleteConfirm({
                          type: 'amc',
                          id: amc._id,
                          name: amc.name
                        });
                      }
                    }}
                    title="Delete AMC"
                  >
                    <Trash2 size={16} />
                  </button>
                  <button
                    className="btn-expand"
                    onClick={() => setExpandedAmc(expandedAmc === amc._id ? null : amc._id)}
                  >
                    {expandedAmc === amc._id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedAmc === amc._id && (
                <div className="amc-expanded">
                  {/* PDF Templates */}
                  <div className="templates-section">
                    <h4>📄 PDF Templates</h4>
                    {amc.pdfTemplates && amc.pdfTemplates.length > 0 ? (
                      <div className="templates-list">
                        {amc.pdfTemplates.map(template => (
                          <div key={template._id} className={`template-item ${amc.name === 'BAJAJ' ? 'protected' : ''}`}>
                            <span className="template-name">{template.filename}</span>
                            <span className="template-size">
                              {(template.size / 1024).toFixed(1)} KB
                            </span>
                            {amc.name === 'BAJAJ' ? (
                              <span className="template-badge">Default</span>
                            ) : (
                              <button
                                className="btn-delete"
                                onClick={() => setDeleteConfirm({
                                  id: template._id,
                                  type: 'pdf',
                                  name: template.filename,
                                  amcId: amc._id
                                })}
                                title="Delete template"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-templates">No PDF templates uploaded</p>
                    )}
                  </div>

                  {/* JSON Schemas */}
                  <div className="templates-section">
                    <h4>📋 JSON Schemas</h4>
                    {amc.jsonTemplates && amc.jsonTemplates.length > 0 ? (
                      <div className="templates-list">
                        {amc.jsonTemplates.map(schema => (
                          <div key={schema._id} className={`template-item ${amc.name === 'BAJAJ' ? 'protected' : ''}`}>
                            <span className="template-name">{schema.filename}</span>
                            <span className="template-size">
                              {(schema.size / 1024).toFixed(1)} KB
                            </span>
                            {amc.name === 'BAJAJ' ? (
                              <span className="template-badge">Default</span>
                            ) : (
                              <button
                                className="btn-delete"
                                onClick={() => setDeleteConfirm({
                                  id: schema._id,
                                  type: 'json',
                                  name: schema.filename,
                                  amcId: amc._id
                                })}
                                title="Delete schema"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-templates">No JSON schemas uploaded</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create AMC Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Create New AMC</h2>
            <div className="form-group">
              <label>AMC Name *</label>
              <input
                type="text"
                value={newAmcName}
                onChange={(e) => setNewAmcName(e.target.value)}
                placeholder="e.g., ICICI, HDFC, SBI"
                disabled={creatingAmc}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newAmcDescription}
                onChange={(e) => setNewAmcDescription(e.target.value)}
                placeholder="Optional description"
                rows="3"
                disabled={creatingAmc}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowCreateModal(false)}
                disabled={creatingAmc}
              >
                Cancel
              </button>
              <button
                className="btn-create"
                onClick={handleCreateAmc}
                disabled={creatingAmc || !newAmcName.trim()}
              >
                {creatingAmc ? 'Creating...' : 'Create AMC'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Template Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Upload Template to {selectedAmc?.name}</h2>
            <div className="form-group">
              <label>Type *</label>
              <div className="type-selector">
                <button
                  className={`type-btn ${uploadType === 'pdf' ? 'active' : ''}`}
                  onClick={() => setUploadType('pdf')}
                >
                  📄 PDF Template
                </button>
                <button
                  className={`type-btn ${uploadType === 'json' ? 'active' : ''}`}
                  onClick={() => setUploadType('json')}
                >
                  📋 JSON Schema
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>Select File *</label>
              <input
                type="file"
                accept={uploadType === 'pdf' ? '.pdf' : '.json'}
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                disabled={uploading}
              />
              {uploadFile && (
                <p className="file-info">Selected: {uploadFile.name}</p>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="btn-upload"
                onClick={handleUploadTemplate}
                disabled={uploading || !uploadFile}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-danger" onClick={e => e.stopPropagation()}>
            <h2>
              {deleteConfirm.type === 'amc'
                ? 'Delete AMC?'
                : `Delete ${deleteConfirm.type.toUpperCase()}?`}
            </h2>
            {deleteConfirm.type === 'amc' ? (
              <>
                <p>Are you sure you want to delete the AMC "{deleteConfirm.name}"?</p>
                <p className="warning">This will also delete ALL templates and schemas in this AMC. This action cannot be undone.</p>
              </>
            ) : (
              <>
                <p>Are you sure you want to delete "{deleteConfirm.name}"?</p>
                <p className="warning">This action cannot be undone.</p>
              </>
            )}
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={() => {
                  if (deleteConfirm.type === 'amc') {
                    handleDeleteAmc(deleteConfirm.id, deleteConfirm.name);
                  } else {
                    handleDeleteTemplate(deleteConfirm.amcId, deleteConfirm.id, deleteConfirm.type);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AMCManagement;
